// SPDX-License-Identifier: MIT

// Built-in tracker loaders for common third-party pixels/tools. Each factory
// takes the site's ID and returns { onAccept, onReject, onRouteChange } to wire
// onto a consent category via the `trackers` option. The plugin knows HOW to
// load each tracker; the site supplies WHICH id — so the plugin stays generic.
//
// onAccept loads + records the entry page view (idempotent), onReject tears the
// tracker down (drops its globals/script/cookies so no further events fire),
// onRouteChange fires a page view on SPA navigation. All run in the browser when
// the category's consent is granted.
//
// Teardown is best-effort and never reloads the page. For pixels like Facebook
// this stops all explicit and future page-view events, but any automatic-event
// listeners the pixel script attached on load persist until the visitor's next
// full page load — the trade-off for not reloading on withdrawal.

/* eslint-disable */

// Deletes window globals (falling back to undefined if delete is disallowed).
const dropGlobals = (keys) => {
  keys.forEach((k) => {
    try {
      delete window[k];
    } catch (e) {
      window[k] = undefined;
    }
  });
};

// Removes <script> tags whose src contains `match`.
const removeScripts = (match) => {
  if (!document.querySelectorAll) return;
  document.querySelectorAll('script[src*="' + match + '"]').forEach((s) => {
    if (s.parentNode) s.parentNode.removeChild(s);
  });
};

// Expires the named cookies on the host and each parent domain, so an identifier
// set on a parent domain (e.g. _fbp on .example.com) is actually removed.
const clearCookies = (names) => {
  const host = (window.location && window.location.hostname) || '';
  const parts = host.split('.');
  const domains = [null]; // host-only, no domain attribute
  for (let i = 0; i < parts.length - 1; i += 1) {
    domains.push('.' + parts.slice(i).join('.'));
  }
  names.forEach((name) => {
    domains.forEach((domain) => {
      document.cookie =
        name + '=; path=/; max-age=0' + (domain ? '; domain=' + domain : '');
    });
  });
};

// Facebook Pixel — gtag/Consent-Mode-independent, so it must be gated here.
const facebookPixel = (pixelId) => ({
  onAccept() {
    if (!window.fbq) {
      !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      window.fbq('init', pixelId);
    }
    window.fbq('track', 'PageView');
  },
  onReject() {
    // Tear down without reloading: drop the globals so no further fbq() calls
    // (including our SPA page views) fire, remove the injected script, and clear
    // the pixel's cookies.
    if (!window.fbq) return;
    dropGlobals(['fbq', '_fbq']);
    removeScripts('connect.facebook.net');
    clearCookies(['_fbp', '_fbc']);
  },
  onRouteChange() {
    if (window.fbq) window.fbq('track', 'PageView');
  },
});

// Reddit Pixel.
const redditPixel = (pixelId) => ({
  onAccept() {
    if (!window.rdt) {
      !(function (w, d) {
        if (!w.rdt) {
          var p = (w.rdt = function () {
            p.sendEvent ? p.sendEvent.apply(p, arguments) : p.callQueue.push(arguments);
          });
          p.callQueue = [];
          var t = d.createElement('script');
          t.src = 'https://www.redditstatic.com/ads/pixel.js';
          t.async = !0;
          var s = d.getElementsByTagName('script')[0];
          s.parentNode.insertBefore(t, s);
        }
      })(window, document);
      window.rdt('init', pixelId);
    }
    window.rdt('track', 'PageVisit');
  },
  onReject() {
    // Tear down without reloading — see the Facebook note above.
    if (!window.rdt) return;
    dropGlobals(['rdt']);
    removeScripts('redditstatic.com/ads/pixel.js');
    clearCookies(['_rdt_uuid']);
  },
  onRouteChange() {
    if (window.rdt) window.rdt('track', 'PageVisit');
  },
});

// Intercom messenger. Has a clean shutdown, so no reload needed on reject.
const intercom = (appId) => ({
  onAccept() {
    window.intercomSettings = { app_id: appId };
    (function () {
      var w = window;
      var ic = w.Intercom;
      if (typeof ic === 'function') {
        ic('reattach_activator');
        ic('update', w.intercomSettings);
      } else {
        var d = document;
        var i = function () {
          i.c(arguments);
        };
        i.q = [];
        i.c = function (args) {
          i.q.push(args);
        };
        w.Intercom = i;
        var l = function () {
          var s = d.createElement('script');
          s.type = 'text/javascript';
          s.async = true;
          s.src = 'https://widget.intercom.io/widget/' + appId;
          var x = d.getElementsByTagName('script')[0];
          x.parentNode.insertBefore(s, x);
        };
        if (document.readyState === 'complete') {
          l();
        } else if (w.attachEvent) {
          w.attachEvent('onload', l);
        } else {
          w.addEventListener('load', l, false);
        }
      }
    })();
    window.Intercom('boot', { app_id: appId });
  },
  onReject() {
    if (window.Intercom) window.Intercom('shutdown');
  },
  onRouteChange() {
    if (window.Intercom) window.Intercom('update');
  },
});

/* eslint-enable */

// Merges several trackers into one { onAccept, onReject, onRouteChange } so a
// single consent category can gate multiple trackers (e.g. marketing = FB + Reddit).
const combineTrackers = (trackers) => ({
  onAccept() {
    trackers.forEach((t) => t.onAccept && t.onAccept());
  },
  onReject() {
    trackers.forEach((t) => t.onReject && t.onReject());
  },
  onRouteChange() {
    trackers.forEach((t) => t.onRouteChange && t.onRouteChange());
  },
});

module.exports = { facebookPixel, redditPixel, intercom, combineTrackers };
